import { endOfToday, startOfDay, subWeeks } from 'date-fns';
import { PseudoTrainingOutcome } from 'enums/training.enums';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { TrainingHelper } from 'lib_ts/classes/training.helper';
import { BallType } from 'lib_ts/enums/machine.enums';
import { TrainingOutcome } from 'lib_ts/enums/training.enums';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import {
  IItrLogExt,
  ITrainingLogExt,
  ITrainingPerformanceFilters,
} from 'lib_ts/interfaces/training/i-training-log';
import { isEqual } from 'lodash';
import { ShotsService } from 'services/shots.service';
import { TrainingLogsService } from 'services/training-logs.service';
import { create } from 'zustand';

export const TRAINING_LOG_LIMIT_OPTIONS: IOption[] = [25, 50, 100, 200].map(
  (n) => ({
    label: `Last ${n} attempts`,
    value: n.toFixed(0),
  })
);

type FiltersWithOutcomes = ITrainingPerformanceFilters & {
  pseudoOutcome: PseudoTrainingOutcome | undefined;
};

interface ITrainingPerformanceState {
  loading: boolean;
  logs: ITrainingLogExt[];

  // all shots from all iterations of the selected log (if any)
  logShots: IMachineShot[];

  filters: FiltersWithOutcomes;

  showTargets: boolean;
  showThresholds: boolean;

  selectedLog: Nullable<ITrainingLogExt>;
  selectedIteration: Nullable<IItrLogExt>;
  selectedShotIds: string[];
}

interface ITrainingPerformanceActions {
  readonly initialize: (
    machineId: string,
    ballType: Nullable<BallType>
  ) => void;

  readonly mergeFilters: (value: Partial<FiltersWithOutcomes>) => void;

  readonly setSelectedLog: (value: Nullable<ITrainingLogExt>) => void;
  readonly setSelectedIteration: (value: Nullable<IItrLogExt>) => void;

  // b/c of staleness, otherwise only the most recent ID would get added
  readonly addSelectedShotIds: (value: string[]) => void;
  readonly removeSelectedShotIds: (value: string[]) => void;
  readonly resetSelectedShotIds: () => void;
}

type ITrainingPerformanceStore = ITrainingPerformanceState &
  ITrainingPerformanceActions;

const generateDefaultFilters = (): FiltersWithOutcomes => {
  const end = endOfToday();
  const start = startOfDay(subWeeks(end, 2));

  return {
    machineID: '',
    period: {
      start: start.toISOString(),
      end: end.toISOString(),
    },
    limit: parseInt(TRAINING_LOG_LIMIT_OPTIONS[0].value),
    ball_type: null,
    pitchTypes: [],
    pseudoOutcome: undefined,
  };
};

const initialState: ITrainingPerformanceState = {
  loading: false,

  logs: [],
  logShots: [],

  filters: generateDefaultFilters(),

  showTargets: true,
  showThresholds: true,

  selectedLog: null,
  selectedIteration: null,
  selectedShotIds: [],
};

const useTrainingPerformanceStore = create<ITrainingPerformanceStore>(
  (set, get) => ({
    ...initialState,

    mergeFilters: async (value: Partial<ITrainingPerformanceFilters>) => {
      const { filters: currentFilters } = get();

      const mergedFilters = { ...currentFilters, ...value };

      if (isEqual(currentFilters, mergedFilters)) {
        return;
      }

      set({
        loading: true,
        filters: mergedFilters,
      });

      const results =
        await TrainingLogsService.getInstance().search(mergedFilters);

      const nextLogs = results?.filter((log) => {
        if (!mergedFilters.pseudoOutcome) {
          return true;
        }

        const outcome = TrainingHelper.getLogOutcome(log);

        switch (mergedFilters.pseudoOutcome) {
          case PseudoTrainingOutcome.Complete: {
            return [
              TrainingOutcome.PT_Complete,
              TrainingOutcome.QT_Complete,
            ].includes(outcome);
          }

          case PseudoTrainingOutcome.GiveUp: {
            return [
              TrainingOutcome.PT_GiveUp,
              TrainingOutcome.QT_GiveUp,
            ].includes(outcome);
          }

          case PseudoTrainingOutcome.PT_Failure: {
            return outcome === TrainingOutcome.PT_Failure;
          }

          default: {
            console.warn(
              `Encountered unknown pseudo outcome: ${mergedFilters.pseudoOutcome}`
            );
            return false;
          }
        }
      });
      set({
        logs: nextLogs ?? [],
        loading: false,
      });
    },

    initialize: (machineID: string, ballType: Nullable<BallType>) => {
      const { mergeFilters } = get();

      // init ball type filter with the current machine ball type
      mergeFilters({
        ...generateDefaultFilters(),
        machineID: machineID,
        ball_type: ballType,
      });

      // reset any selection from before
      set({
        selectedLog: null,
        selectedIteration: null,
        selectedShotIds: [],
        logShots: [],
      });
    },

    setSelectedLog: async (value) => {
      set({
        selectedLog: value,
        selectedIteration: null, // reset any selections
        selectedShotIds: [], // reset any selections

        logShots: value
          ? // one load for all shots from all iterations for this training log
            await ShotsService.getInstance().getByIds(
              value.iterations.flatMap((itr) => itr.shotIDs)
            )
          : [],
      });
    },

    setSelectedIteration: (value) => {
      set({
        selectedIteration: value,
        selectedShotIds: [], // reset any selections
      });
    },

    addSelectedShotIds: (value) => {
      set((state) => ({
        selectedShotIds: ArrayHelper.unique([
          ...state.selectedShotIds,
          ...value,
        ]),
      }));
    },

    removeSelectedShotIds: (value) => {
      set((state) => ({
        selectedShotIds: state.selectedShotIds.filter(
          (m) => !value.includes(m)
        ),
      }));
    },

    resetSelectedShotIds: () => {
      set({
        selectedShotIds: [],
      });
    },
  })
);

export default useTrainingPerformanceStore;
